import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints.styles';

export const GuideListContainer = styled.div`
  width: 100%;
  max-width: var(--max-width);

  .guide-intro {
    margin-bottom: 4rem;
    word-break: keep-all;
    line-height: 1.7;
    color: var(--color-text-footer);
    font-size: 1.7rem;

    h1 {
      margin-bottom: 1.2rem;
      color: #222;
      font-size: 3.2rem;
      font-weight: 700;
    }

    p + p {
      margin-top: 1rem;
    }
  }

  .guide-list {
    display: grid;
    gap: 2rem;
  }

  .guide-card {
    display: block;
    padding: 2.4rem 2.8rem;
    border: 0.1rem solid var(--color-border-light);
    border-radius: 0.8rem;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      border-color: var(--color-primary);
      box-shadow: 0 0.8rem 2rem rgba(0, 0, 0, 0.06);
    }
  }

  .guide-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem 1.6rem;
    margin-bottom: 1rem;
    color: #888;
    font-size: 1.4rem;

    .guide-card__category {
      color: var(--color-primary);
      font-weight: 700;
    }
  }

  .guide-card__title {
    margin-bottom: 0.8rem;
    font-size: 2.2rem;
    font-weight: 700;
    word-break: keep-all;
  }

  .guide-card__desc {
    color: var(--color-text-footer);
    font-size: 1.6rem;
    line-height: 1.6;
    word-break: keep-all;
  }

  @media ${breakpoints.mobile} {
    .guide-intro h1 {
      font-size: 2.4rem;
    }

    .guide-card {
      padding: 2rem;
    }

    .guide-card__title {
      font-size: 1.9rem;
    }
  }
`;

export const GuideDetailContainer = styled.article`
  width: 100%;
  max-width: 84rem;
  margin: 0 auto;
  word-break: keep-all;

  .guide-detail__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem 1.6rem;
    margin-bottom: 1.6rem;
    color: #888;
    font-size: 1.4rem;

    .guide-detail__category {
      color: var(--color-primary);
      font-weight: 700;
    }
  }

  h1 {
    margin-bottom: 1.6rem;
    font-size: 3.4rem;
    font-weight: 700;
    line-height: 1.35;
  }

  .guide-detail__lead {
    margin-bottom: 4rem;
    padding-bottom: 2.4rem;
    border-bottom: 0.1rem solid var(--color-border-light);
    color: var(--color-text-footer);
    font-size: 1.8rem;
    line-height: 1.7;
  }

  section {
    margin-bottom: 3.6rem;

    h2 {
      margin-bottom: 1.4rem;
      font-size: 2.2rem;
      font-weight: 700;
    }

    p {
      color: #333;
      font-size: 1.7rem;
      line-height: 1.85;

      & + p {
        margin-top: 1.2rem;
      }
    }
  }

  .guide-detail__nav {
    display: flex;
    justify-content: space-between;
    gap: 1.6rem;
    margin-top: 4rem;
    padding-top: 2.4rem;
    border-top: 0.1rem solid var(--color-border-light);

    a {
      color: var(--color-primary);
      font-weight: 600;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media ${breakpoints.mobile} {
    h1 {
      font-size: 2.4rem;
    }

    section h2 {
      font-size: 1.9rem;
    }

    section p,
    .guide-detail__lead {
      font-size: 1.6rem;
    }
  }
`;

export const FaqContainer = styled.div`
  width: 100%;
  max-width: 90rem;
  margin: 0 auto;
  word-break: keep-all;

  .faq-intro {
    margin-bottom: 4rem;

    h1 {
      margin-bottom: 1.2rem;
      font-size: 3.2rem;
      font-weight: 700;
    }

    p {
      color: var(--color-text-footer);
      font-size: 1.7rem;
      line-height: 1.7;

      & + p {
        margin-top: 1rem;
      }
    }
  }

  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .faq-item {
    border: 0.1rem solid var(--color-border-light);
    border-radius: 0.8rem;
    overflow: hidden;
  }

  .faq-item__question {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.6rem;
    padding: 2rem 2.4rem;
    text-align: left;
    font-size: 1.8rem;
    font-weight: 700;
    background: var(--color-grey-bg);
    cursor: pointer;

    i {
      color: var(--color-primary);
      font-size: 1.6rem;
    }
  }

  .faq-item__answer {
    padding: 0 2.4rem 2.2rem;
    background: #fff;

    p {
      color: #333;
      font-size: 1.65rem;
      line-height: 1.8;

      & + p {
        margin-top: 1rem;
      }
    }
  }

  .faq-contact {
    margin-top: 4rem;
    padding: 2.4rem;
    border-radius: 0.8rem;
    background: var(--color-grey-bg);
    line-height: 1.7;
    font-size: 1.6rem;

    a {
      color: var(--color-primary);
      font-weight: 600;
    }
  }

  @media ${breakpoints.mobile} {
    .faq-intro h1 {
      font-size: 2.4rem;
    }

    .faq-item__question {
      padding: 1.6rem;
      font-size: 1.6rem;
    }

    .faq-item__answer {
      padding: 0 1.6rem 1.8rem;
    }
  }
`;

export const MainGuideSection = styled.div`
  width: 100%;
  max-width: var(--max-width);

  .main-guide__intro {
    max-width: 72rem;
    margin: 0 auto 4rem;
    text-align: center;
    word-break: keep-all;
    color: var(--color-text-footer);
    font-size: 1.7rem;
    line-height: 1.7;
  }

  .main-guide__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }

  .main-guide__card {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 2.4rem;
    border: 0.1rem solid var(--color-border-light);
    border-radius: 0.8rem;
    height: 100%;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: var(--color-primary);
    }

    small {
      color: var(--color-primary);
      font-weight: 700;
      font-size: 1.3rem;
    }

    strong {
      font-size: 1.9rem;
      line-height: 1.4;
      word-break: keep-all;
    }

    p {
      flex: 1;
      color: var(--color-text-footer);
      font-size: 1.5rem;
      line-height: 1.6;
      word-break: keep-all;
    }

    span {
      color: var(--color-primary);
      font-weight: 600;
      font-size: 1.4rem;
    }
  }

  .main-guide__more {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 3rem;

    a {
      padding: 1.2rem 2.4rem;
      border-radius: 999px;
      background: var(--color-primary);
      color: #fff;
      font-weight: 600;
    }

    a:last-child {
      background: #333;
    }
  }

  @media ${breakpoints.tablet} {
    .main-guide__grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media ${breakpoints.mobile} {
    .main-guide__grid {
      grid-template-columns: 1fr;
    }
  }
`;
